import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { deleteCandidate, getCandidate, getCandidateStageCounts } from "../services/candidateApi";
import { getVacancyOptions } from "../services/vacancyApi";
import { getHHEmployerVacancies } from "../services/hhImportApi";
import { mergeHhVacancyFilterOptions } from "../utils/hhVacancyFilterOptions";
import { useAlertContext } from "../context/AlertContext";
import MainLayout from "../layout/MainLayout";
import CandidateForm from "../components/candidates/CandidateForm";
import CandidateDetails from "../components/candidates/CandidateDetails";
import CandidatesToolbar, { FunnelTabs } from "../components/candidates/CandidatesToolbar";
import CandidateListView from "../components/candidates/CandidateListView";
import CandidateQuickActionButtons from "../components/candidates/CandidateQuickActionButtons";
import { CandidateBulkBar, QuickActionsSettings } from "../components/candidates/CandidateBulkActions";
import CandidateViewSettings from "../components/candidates/CandidateViewSettings";
import useCandidateActions from "../components/candidates/useCandidateActions";
import { buildCandidateColumns, DEFAULT_CANDIDATE_COLUMNS } from "../components/candidates/candidateColumns";
import ConfigurableTable from "../components/table/ConfigurableTable";
import ColumnSettingsPanel from "../components/table/ColumnSettingsPanel";
import useTablePrefs from "../components/table/useTablePrefs";
import { emptyTablePrefs, visibleColumnIds, withColumns } from "../components/table/tablePrefs";
import useUiPrefs from "../hooks/useUiPrefs";
import useLongPress from "../hooks/useLongPress";
import { useCandidatesList } from "../hooks/useCandidatesList";
import { isArchiveStatus } from "../utils/candidateStatuses";
import {
    PAGE_SIZES, filtersFromTableSort, filtersToRequest, normalizeFilters, normalizeViewPrefs,
    resetFilters, tabScope, tableSortFromFilters,
} from "../utils/candidateFunnel";

/**
 * «Кандидаты» (правка №4/№14, Чат 1). [Claude] 2026-11-03
 * Funnel tabs with counters, compact filters, two views — a list like hh.ru and a configurable table.
 * View, quick actions, page size, tab and filters are remembered for each account
 * (ui-preferences "candidates_view"; table columns — "candidates_table").
 */
const VIEWS = [
    { key: "list", label: "Список", icon: "M4 6h16M4 12h16M4 18h16" },
    { key: "table", label: "Таблица", icon: "M3 10h18M3 14h18M10 3v18M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" },
];

const PERSISTED = (filters) => {
    const { search, ...rest } = normalizeFilters(filters);
    return rest;
};

export default function CandidatesPage() {
    const linkedCandidateId = useMemo(() => {
        const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        const value = Number(params.get("candidate_id"));
        return Number.isInteger(value) && value > 0 ? value : null;
    }, []);
    const { showAlert } = useAlertContext();
    const { prefs: view, update: updateView, loaded: viewLoaded } = useUiPrefs("candidates_view", normalizeViewPrefs);
    const { prefs: tablePrefs, update: updateTablePrefs } = useTablePrefs("candidates_table");

    const [filters, setFilters] = useState(() => normalizeFilters(view.filters));
    const [tab, setTab] = useState(view.tab);
    const hydrated = useRef(false);
    const touched = useRef(false);
    const [shownForm, setShownForm] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [editingCandidate, setEditingCandidate] = useState(null);
    const [vacancyOptions, setVacancyOptions] = useState([]);
    const [allVacancyOptions, setAllVacancyOptions] = useState([]);
    const [unboundHhCount, setUnboundHhCount] = useState(0);
    const [vacanciesLoading, setVacanciesLoading] = useState(true);
    const [counts, setCounts] = useState(null);
    const [countsTick, setCountsTick] = useState(0);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [commentFor, setCommentFor] = useState(null);
    const [showColumns, setShowColumns] = useState(false);

    // Settings from the server arrive after the first render — take them once.
    useEffect(() => {
        if (!viewLoaded || hydrated.current) return;
        hydrated.current = true;
        if (touched.current) {
            // The user already changed something — keep it; it will be saved.
            updateView({ tab, filters: PERSISTED(filters) });
            return;
        }
        setFilters((current) => ({ ...normalizeFilters(view.filters), search: current.search }));
        setTab(view.tab);
    }, [viewLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!hydrated.current) return;
        updateView({ tab, filters: PERSISTED(filters) });
    }, [tab, filters, updateView]);

    const changeFilters = useCallback((patch) => {
        touched.current = true;
        setFilters((prev) => normalizeFilters({ ...prev, ...patch }));
    }, []);

    const scope = tabScope(tab);
    const request = useMemo(() => ({ ...filtersToRequest(filters), stage: scope.stage || undefined }), [filters, scope.stage]);
    const vacancyIds = useMemo(() => filters.vacancy_ids.map((id) => Number(id) || id), [filters.vacancy_ids]);

    const onListError = useCallback((e) => {
        console.error("Ошибка загрузки кандидатов:", e);
        showAlert("Ошибка загрузки кандидатов: " + (e.message || "Unknown error"), "error");
    }, [showAlert]);

    const {
        candidates, setCandidates, listLoading, page, totalPages, total, goToPage, reloadFirstPage, loadCandidatesPage,
    } = useCandidatesList({
        statusFilter: null,
        debouncedSearch: filters.search,
        roleFilter: "",
        vacancyIds,
        onlyPerfect: filters.perfect,
        onlyNotSync: filters.not_sync,
        categoryFilter: scope.category,
        includeArchived: scope.includeArchived,
        extraFilters: request,
        perPage: view.page_size,
        onError: onListError,
    });

    const reloadCurrent = useCallback(() => {
        if (typeof loadCandidatesPage === "function") loadCandidatesPage(page);
        else reloadFirstPage();
        setCountsTick((n) => n + 1);
    }, [loadCandidatesPage, page, reloadFirstPage]);

    const actions = useCandidateActions({ onChange: reloadCurrent });
    const rows = useMemo(() => candidates.map(actions.withPatch), [candidates, actions.withPatch]);

    // Tab counters follow every filter except the tab itself.
    const countsKey = JSON.stringify([filters.search, filters.vacancy_ids, filters.perfect, filters.not_sync, filtersToRequest(filters), countsTick]);
    useEffect(() => {
        const ac = new AbortController();
        const timer = setTimeout(() => {
            getCandidateStageCounts({
                search: filters.search, vacancyIds, isPerfect: filters.perfect, isNotSync: filters.not_sync, ...filtersToRequest(filters),
            }, { signal: ac.signal })
                .then((data) => setCounts(data && typeof data === "object" ? data : null))
                .catch((e) => { if (e?.name !== "AbortError") setCounts(null); });
        }, 250);
        return () => { clearTimeout(timer); ac.abort(); };
    }, [countsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Selection is kept only for candidates on the current page.
    useEffect(() => {
        setSelectedIds((prev) => {
            const onPage = new Set(candidates.map((c) => c.id));
            const next = new Set([...prev].filter((id) => onPage.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [candidates]);

    useEffect(() => {
        if (!linkedCandidateId) return undefined;
        let active = true;
        getCandidate(linkedCandidateId)
            .then((candidate) => { if (active) setSelectedCandidate(candidate); })
            .catch((e) => { if (active) showAlert(`Не удалось открыть карточку кандидата: ${e.message || e}`, "error"); });
        return () => { active = false; };
    }, [linkedCandidateId, showAlert]);

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            setVacanciesLoading(true);
            try {
                const [localList, hhData] = await Promise.all([
                    getVacancyOptions({ hhOnly: false, signal: ac.signal }),
                    getHHEmployerVacancies({ archived: false, page: 0, per_page: 100, all_accessible: true }).catch((e) => {
                        if (e?.name === "AbortError") throw e;
                        console.warn("HH employer vacancies for candidates filter failed", e);
                        return { items: [] };
                    }),
                ]);
                if (ac.signal.aborted) return;
                const local = Array.isArray(localList) ? localList : [];
                setAllVacancyOptions(local);
                const { options, unboundHhCount: unbound } = mergeHhVacancyFilterOptions(local, hhData?.items || []);
                setVacancyOptions(options);
                setUnboundHhCount(unbound);
            } catch (e) {
                if (e?.name === "AbortError") return;
                console.error("Не удалось загрузить вакансии", e);
                if (!ac.signal.aborted) {
                    setVacancyOptions([]);
                    setAllVacancyOptions([]);
                    setUnboundHhCount(0);
                    showAlert("Фильтр вакансий временно недоступен: " + (e.message || e), "warning");
                }
            } finally {
                if (!ac.signal.aborted) setVacanciesLoading(false);
            }
        })();
        return () => ac.abort();
    }, [showAlert]);

    const vacancyName = useCallback((id) => {
        const key = String(id);
        const found = vacancyOptions.find((v) => String(v.id) === key) || allVacancyOptions.find((v) => String(v.id) === key);
        return found?.name || `Вакансия #${id}`;
    }, [vacancyOptions, allVacancyOptions]);

    const selectedVacancyForTests = useMemo(() => {
        if (filters.vacancy_ids.length !== 1) return null;
        const selectedId = String(filters.vacancy_ids[0]);
        return (allVacancyOptions.length ? allVacancyOptions : vacancyOptions).find((v) => String(v.id) === selectedId)
            || vacancyOptions.find((v) => String(v.id) === selectedId) || null;
    }, [allVacancyOptions, filters.vacancy_ids, vacancyOptions]);

    const handleCandidateDeleted = async (id) => {
        if (!id || !window.confirm("Удалить кандидата?")) return;
        try {
            await deleteCandidate(id);
            showAlert("Кандидат удалён", "success");
            if (selectedCandidate?.id === id) setSelectedCandidate(null);
            reloadCurrent();
        } catch (e) {
            showAlert(`Ошибка при удалении: ${e.message || "Unknown error"}`, "error");
        }
    };

    const handleFormSuccess = (saved) => {
        if (saved?.id) {
            setCandidates((prev) => (prev.some((c) => c.id === saved.id) ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev]));
        }
        reloadFirstPage();
        setCountsTick((n) => n + 1);
        setShownForm(false);
        setEditingCandidate(null);
    };

    const onAction = useCallback((key, candidate) => {
        switch (key) {
        case "interview": actions.openInterview(candidate); break;
        case "test": actions.openTest(candidate); break;
        case "offer": actions.openOffer(candidate); break;
        case "consider": actions.setStatus(candidate, "собес: ожидает решения"); break;
        case "reject": actions.reject(candidate); break;
        case "comment": setCommentFor(candidate.id); break;
        case "edit": setEditingCandidate(candidate); setShownForm(true); break;
        case "delete": handleCandidateDeleted(candidate.id); break;
        default: break;
        }
    }, [actions]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSelected = useCallback((id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    }), []);

    const archivedView = tab === "archive";
    const selectedRows = rows.filter((c) => selectedIds.has(c.id));
    // Выбор нескольких (Света 04.11): зажать строку → режим выбора, дальше выбираем кликом; Esc — снять.
    const selectionMode = selectedIds.size > 0;
    const bindLongPress = useLongPress(useCallback((row) => toggleSelected(row.id), [toggleSelected]));
    const openOrPick = (row) => (selectionMode ? toggleSelected(row.id) : setSelectedCandidate(row));
    useEffect(() => {
        if (!selectionMode) return undefined;
        const onKey = (event) => { if (event.key === "Escape") setSelectedIds(new Set()); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectionMode]);

    const catalog = useMemo(() => buildCandidateColumns({ selected: selectedIds, onToggle: toggleSelected }), [selectedIds, toggleSelected]);
    const visibleIds = visibleColumnIds(catalog, tablePrefs, DEFAULT_CANDIDATE_COLUMNS);
    const tableView = useMemo(() => ({ ...tablePrefs, sort: tableSortFromFilters(filters) }), [tablePrefs, filters]);
    const onTablePrefs = (next) => {
        const { sort, ...rest } = next || {};
        if (JSON.stringify(sort || null) !== JSON.stringify(tableView.sort || null)) changeFilters(filtersFromTableSort(sort));
        updateTablePrefs({ ...emptyTablePrefs(), ...rest, sort: null });
    };

    const empty = <div className="flex flex-col items-center py-10 text-center">
        <h3 className="text-base font-medium text-slate-800">Кандидаты не найдены</h3>
        <p className="mt-1 text-sm text-slate-500">Измените фильтры или откройте другую вкладку.</p>
    </div>;

    const loadingBlock = <div className="flex items-center justify-center py-16 text-slate-400">
        <svg className="mr-3 h-6 w-6 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
        Загружаем кандидатов…
    </div>;

    return (
        <MainLayout className="flex h-full flex-col overflow-y-auto" contentClassName="max-w-none">
            <div className="mb-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-baseline gap-3">
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Кандидаты</h1>
                        {typeof total === "number" ? <span className="text-sm text-slate-500" data-testid="candidates-total">найдено: {total}</span> : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="group" aria-label="Вид" data-testid="candidates-view-switch">
                            {VIEWS.map((v) => <button key={v.key} type="button" onClick={() => updateView({ view: v.key })} aria-pressed={view.view === v.key}
                                data-view={v.key}
                                className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium ${view.view === v.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={v.icon} /></svg>
                                {v.label}
                            </button>)}
                        </div>
                        {view.view === "table" ? <button type="button" onClick={() => setShowColumns(true)} data-testid="candidate-columns-button"
                            className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">Колонки</button> : null}
                        {view.view === "list" ? <CandidateViewSettings blocks={view.list_blocks} density={view.density} onChange={updateView} /> : null}
                        <QuickActionsSettings value={view.quick_actions} onChange={(keys) => updateView({ quick_actions: keys })} />
                        {selectedVacancyForTests ? (
                            <Link
                                to={`/vacancies/${encodeURIComponent(selectedVacancyForTests.id)}/tests`}
                                state={{ vacancyName: selectedVacancyForTests.name }}
                                data-testid="selected-vacancy-tests-link"
                                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                                title={`Настроить тесты для вакансии «${selectedVacancyForTests.name}»`}
                            >
                                Тесты вакансии
                            </Link>
                        ) : null}
                        <button type="button" onClick={() => { setEditingCandidate(null); setShownForm(true); }}
                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-900 px-3 text-sm font-medium text-white hover:bg-slate-800">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Добавить кандидата
                        </button>
                    </div>
                </div>

                <FunnelTabs tab={tab} counts={counts} onChange={(key) => { touched.current = true; setTab(key); setSelectedIds(new Set()); }} />

                <CandidatesToolbar
                    filters={filters}
                    onChange={changeFilters}
                    onReset={() => { touched.current = true; setFilters((prev) => resetFilters(prev)); }}
                    vacancyOptions={vacancyOptions}
                    vacanciesLoading={vacanciesLoading}
                    unboundHhCount={unboundHhCount}
                    vacancyName={vacancyName}
                />

                <CandidateBulkBar
                    selected={selectedRows}
                    pageCount={rows.length}
                    allOnPage={rows.length > 0 && selectedRows.length === rows.length}
                    onSelectPage={() => setSelectedIds(new Set(rows.map((c) => c.id)))}
                    onClear={() => setSelectedIds(new Set())}
                    onDone={() => { setSelectedIds(new Set()); reloadCurrent(); }}
                    showAlert={showAlert}
                />
                {!selectionMode && rows.length ? <p className="text-xs text-slate-400" data-testid="candidates-select-hint">Чтобы выбрать несколько — зажмите строку, дальше выбирайте кликом. Esc — снять выбор.</p> : null}
            </div>

            {listLoading && candidates.length === 0 ? loadingBlock : view.view === "table" ? (
                <div className={`rounded-2xl border border-slate-200/70 bg-white ${listLoading ? "opacity-60" : ""}`}>
                    <ConfigurableTable
                        testId="candidates-table"
                        catalog={catalog}
                        defaultColumns={DEFAULT_CANDIDATE_COLUMNS}
                        rows={rows}
                        prefs={tableView}
                        onPrefsChange={onTablePrefs}
                        onRowClick={openOrPick}
                        rowHandlers={bindLongPress}
                        selectedKeys={selectedIds}
                        actions={(row) => <CandidateQuickActionButtons compact candidate={row} keys={view.quick_actions}
                            archived={archivedView || isArchiveStatus(row.status)} busy={actions.busyId === row.id} onAction={onAction} />}
                        empty={empty}
                    />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-slate-200/70 bg-white">{empty}</div>
            ) : (
                <div className={listLoading ? "opacity-60" : ""}>
                    <CandidateListView
                        candidates={rows}
                        selected={selectedIds}
                        onToggle={toggleSelected}
                        onOpen={openOrPick}
                        bindLongPress={bindLongPress}
                        selectionMode={selectionMode}
                        quickActions={view.quick_actions}
                        archivedView={archivedView}
                        busyId={actions.busyId}
                        onAction={onAction}
                        commentFor={commentFor}
                        onCommentClose={() => setCommentFor(null)}
                        onCommentSave={actions.saveComment}
                        blocks={view.list_blocks}
                        density={view.density}
                    />
                </div>
            )}

            {view.view === "table" && commentFor ? (() => {
                const row = rows.find((c) => c.id === commentFor);
                if (!row) return null;
                return <CommentDialog candidate={row} onClose={() => setCommentFor(null)} onSave={actions.saveComment} />;
            })() : null}

            <nav className="mt-4 flex flex-wrap items-center justify-center gap-2" aria-label="Страницы кандидатов">
                <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 0 || listLoading}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm disabled:opacity-40">Назад</button>
                <span className="px-2 text-sm text-slate-600" data-testid="candidates-page-indicator">
                    Страница {page + 1} из {totalPages || 1}
                </span>
                <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages - 1 || listLoading}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm disabled:opacity-40">Вперёд</button>
                <select value={view.page_size} onChange={(e) => updateView({ page_size: Number(e.target.value) })} aria-label="Записей на странице"
                    className="ml-2 h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-600">
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>по {n}</option>)}
                </select>
            </nav>

            {actions.modals}

            {shownForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
                    <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-0 shadow-2xl ring-1 ring-slate-900/5" onClick={(e) => e.stopPropagation()}>
                        <CandidateForm
                            initialData={editingCandidate}
                            onClose={() => { setShownForm(false); setEditingCandidate(null); }}
                            onCandidateAdded={handleFormSuccess}
                        />
                    </div>
                </div>
            )}

            {showColumns ? <ColumnSettingsPanel
                catalog={catalog}
                visibleIds={visibleIds}
                onChange={(ids) => updateTablePrefs(withColumns(tablePrefs, catalog, ids))}
                onReset={() => updateTablePrefs(emptyTablePrefs())}
                onClose={() => setShowColumns(false)}
            /> : null}

            {selectedCandidate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:p-3" onClick={() => setSelectedCandidate(null)}>
                    <div className="flex h-[96vh] w-full max-w-[1800px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
                            <h2 className="text-lg font-bold text-slate-900">Карточка кандидата</h2>
                            <button type="button" onClick={() => setSelectedCandidate(null)} aria-label="Закрыть"
                                className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-slate-50 p-3 sm:p-4">
                            <CandidateDetails
                                candidate={selectedCandidate}
                                isArchiveView={isArchiveStatus(selectedCandidate.status) || archivedView}
                                onChange={(saved) => {
                                    if (saved?.id) setSelectedCandidate((current) => (current?.id === saved.id ? { ...current, ...saved } : current));
                                    reloadCurrent();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
}

function CommentDialog({ candidate, onClose, onSave }) {
    const [text, setText] = useState(candidate.hr_comment || "");
    const [saving, setSaving] = useState(false);
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
        <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900">Комментарий — {candidate.full_name}</h3>
            <textarea autoFocus rows={4} value={text} onChange={(e) => setText(e.target.value)}
                className="mt-2 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-amber-400" />
            <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100">Отмена</button>
                <button type="button" disabled={saving} onClick={async () => { setSaving(true); const ok = await onSave(candidate, text.trim()); setSaving(false); if (ok) onClose(); }}
                    className="rounded-lg bg-[#e0bb48] px-3 py-1.5 text-sm font-semibold text-black hover:bg-[#d4af3a] disabled:opacity-50">Сохранить</button>
            </div>
        </div>
    </div>;
}
